/**
 * HTTP client for the anonymous public-survey endpoints exposed by
 * `ADP.Surveys.API/Controllers/PublicSurveyController.cs`:
 *
 *   GET  {baseUrl}/SurveyInstances/{publicId}/schema    → resolved SurveyDto
 *   GET  {baseUrl}/SurveyInstances/{publicId}/status    → instance status blob
 *   POST {baseUrl}/SurveyInstances/{publicId}/responses → accepts answer map + meta
 *
 * The controller returns bare `{ Message, Errors? }` JSON on error (it does NOT use
 * the ShiftEntity envelope — that only wraps authenticated admin responses), so the
 * error mapping here is deliberately simple.
 */
export class SurveyClientError extends Error {
    status;
    code;
    serverMessage;
    validationErrors;
    raw;
    constructor(init) {
        super(init.message);
        this.name = 'SurveyClientError';
        this.status = init.status;
        this.code = init.code;
        this.serverMessage = init.serverMessage;
        this.validationErrors = init.validationErrors;
        this.raw = init.raw;
    }
}
export class SurveyClient {
    baseUrl;
    fetchFn;
    constructor(options) {
        this.baseUrl = options.baseUrl.replace(/\/+$/, '');
        const f = options.fetch ?? globalThis.fetch;
        if (!f)
            throw new Error('SurveyClient: no fetch available. Provide options.fetch or run in an environment with a global fetch.');
        // Preserve fetch's `this` binding (important in browsers — globalThis.fetch
        // must be called with `window` as receiver on some engines).
        this.fetchFn = f.bind(globalThis);
    }
    async fetchSchema(publicId) {
        const response = await this.send('GET', `/SurveyInstances/${encodeURIComponent(publicId)}/schema`);
        return this.readJson(response);
    }
    async getStatus(publicId) {
        const response = await this.send('GET', `/SurveyInstances/${encodeURIComponent(publicId)}/status`);
        const body = await this.readJson(response);
        return {
            status: String(body['Status'] ?? body['status'] ?? 'Pending'),
            schemaVersion: Number(body['SchemaVersion'] ?? body['schemaVersion'] ?? 0),
            triggeredAt: (body['TriggeredAt'] ?? body['triggeredAt']),
        };
    }
    async submitResponse(publicId, submission) {
        await this.send('POST', `/SurveyInstances/${encodeURIComponent(publicId)}/responses`, submission);
    }
    async send(method, path, body) {
        let response;
        try {
            response = await this.fetchFn(`${this.baseUrl}${path}`, {
                method,
                headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
                body: body === undefined ? undefined : JSON.stringify(body),
            });
        }
        catch (e) {
            throw new SurveyClientError({
                status: 0,
                code: 'network',
                message: `Network error calling ${method} ${path}: ${e.message ?? e}`,
            });
        }
        if (!response.ok)
            throw await this.toError(response, method, path);
        return response;
    }
    async readJson(response) {
        const text = await response.text();
        if (!text) {
            throw new SurveyClientError({
                status: response.status,
                code: 'parse',
                message: `Empty body from ${response.url}`,
            });
        }
        try {
            return JSON.parse(text);
        }
        catch (e) {
            throw new SurveyClientError({
                status: response.status,
                code: 'parse',
                message: `Could not parse JSON from ${response.url}: ${e.message}`,
                raw: text,
            });
        }
    }
    async toError(response, method, path) {
        const code = response.status === 404
            ? 'notFound'
            : response.status === 410
                ? 'gone'
                : response.status === 409
                    ? 'conflict'
                    : response.status === 400
                        ? 'badRequest'
                        : response.status >= 500
                            ? 'server'
                            : 'server';
        const text = await response.text();
        if (!text) {
            return new SurveyClientError({
                status: response.status,
                code,
                message: `${method} ${path} → ${response.status}`,
            });
        }
        let parsed;
        try {
            parsed = JSON.parse(text);
        }
        catch {
            return new SurveyClientError({
                status: response.status,
                code,
                message: `${method} ${path} → ${response.status}: ${text.slice(0, 200)}`,
                raw: text,
            });
        }
        const serverMessage = (parsed['Message'] ?? parsed['message']);
        const rawErrors = (parsed['Errors'] ?? parsed['errors']);
        const validationErrors = Array.isArray(rawErrors)
            ? rawErrors.flatMap((e) => {
                const qid = (e['QuestionId'] ?? e['questionId']);
                const msg = (e['Message'] ?? e['message']);
                return qid && msg ? [{ questionId: qid, message: msg }] : [];
            })
            : undefined;
        return new SurveyClientError({
            status: response.status,
            code,
            message: `${method} ${path} → ${response.status}${serverMessage ? ': ' + serverMessage : ''}`,
            serverMessage,
            validationErrors: validationErrors && validationErrors.length > 0 ? validationErrors : undefined,
            raw: parsed,
        });
    }
}
//# sourceMappingURL=client.js.map